import { motion } from 'framer-motion';
import { Sparkles, Code2, BookOpen, ArrowRight } from 'lucide-react';

import { LINKS } from '@/lib/constants';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

const productCards = [
  {
    icon: Sparkles,
    title: 'Natural-language building',
    description: 'Sculpt the outcome you want.',
    detail: "Describe what you're trying to build. Iterate on behavior and UX. Keep momentum.",
  },
  {
    icon: Code2,
    title: 'Real software, not demos',
    description: 'Speed with precision.',
    detail:
      'Build things that can ship. Move quickly, but stay intentional about craft and correctness.',
  },
  {
    icon: BookOpen,
    title: 'Learn the workflow',
    description: 'Clear docs, pragmatic guidance.',
    detail:
      'Our docs cover quickstart, integrations, prompt engineering strategies, and deployment.',
    hasCta: true,
  },
];

export function ProductSection() {
  return (
    <section id="product" className="py-24 lg:py-32 px-5 lg:px-12 bg-black scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          {/* Section Label */}
          <motion.p
            variants={itemVariants}
            className="text-xs font-medium text-white/40 uppercase tracking-[0.05em]"
          >
            Product
          </motion.p>

          {/* Headline */}
          <motion.h2
            variants={itemVariants}
            className="mt-4 text-3xl sm:text-4xl lg:text-[40px] font-semibold leading-[1.2] tracking-[-0.01em] text-white max-w-2xl"
          >
            Built for creators, engineers, and everyone in between.
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="mt-6 text-base text-white/60 leading-relaxed max-w-2xl"
          >
            Whether you&apos;re prototyping, shipping, or exploring new experiences, Michelangelo is
            designed to help you go from &quot;idea&quot; to &quot;app&quot; faster, without losing
            quality.
          </motion.p>

          {/* Cards Grid */}
          <motion.div variants={containerVariants} className="mt-12 grid md:grid-cols-3 gap-4">
            {productCards.map((card) => (
              <motion.div
                key={card.title}
                variants={itemVariants}
                className="glass-card glass-card-hover p-6 flex flex-col glass-card-blur"
                style={{ willChange: 'transform' }}
              >
                <card.icon className="w-5 h-5 text-white/70 mb-4" />
                <h3 className="text-base font-semibold text-white">{card.title}</h3>
                <p className="text-sm text-white/50 mt-1">{card.description}</p>
                <p className="text-sm text-white/40 mt-4 leading-relaxed flex-1">{card.detail}</p>
                {card.hasCta && (
                  <a
                    href={LINKS.docs}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white border border-white/20 hover:bg-white/5 transition-all duration-200 w-fit"
                  >
                    Read documentation
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
